import { Link } from "@tanstack/react-router"
import { LucideIcon, ArrowRight, Router, DollarSign, RefreshCw, Bot, Shield } from "lucide-react"

const iconMap: Record<string, LucideIcon> = {
  Router,
  DollarSign,
  RefreshCw,
  Bot,
  Shield,
}

interface UseCaseCardProps {
  title: string
  description: string
  href: string
  icon?: LucideIcon
  iconName?: string
  audience?: string
  benefits?: string[]
}

export function UseCaseCard({
  title,
  description,
  href,
  icon: IconProp,
  iconName,
  audience,
  benefits
}: UseCaseCardProps) {
  const Icon = IconProp || (iconName ? iconMap[iconName] : undefined)

  return (
    <Link
      to={href}
      className="group block rounded-2xl border bg-card overflow-hidden transition-all hover:shadow-lg hover:border-primary/50"
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          {Icon && (
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary flex-shrink-0">
              <Icon className="h-6 w-6" />
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">
              {title}
            </h3>
            {audience && (
              <p className="text-sm text-muted-foreground mt-1">
                For: {audience}
              </p>
            )}
          </div>
        </div>

        <p className="mt-4 text-muted-foreground leading-relaxed">
          {description}
        </p>

        {benefits && benefits.length > 0 && (
          <ul className="mt-4 space-y-2">
            {benefits.slice(0, 3).map((benefit, index) => (
              <li key={index} className="flex items-center gap-2 text-sm">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                {benefit}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-6 flex items-center text-sm font-medium text-primary">
          Explore use case
          <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  )
}

interface UseCaseGridProps {
  children: React.ReactNode
  columns?: 2 | 3
}

export function UseCaseGrid({ children, columns = 2 }: UseCaseGridProps) {
  const gridCols = {
    2: "lg:grid-cols-2",
    3: "lg:grid-cols-3"
  }[columns]

  return (
    <div className={`grid grid-cols-1 gap-6 ${gridCols}`}>
      {children}
    </div>
  )
}
